import React, { useCallback, useRef } from 'react';

import { Box } from '@chakra-ui/react';
import { Button, CollapsibleCard, Download, RadarChart } from '@mesh.ai/rabbit';
import useLocalStorageCards from 'Deer/hooks/useLocalStorageCards';
import { useRouteToNavLevels } from 'Deer/hooks/useRouteToL3';
import { useGetSpiderChartQuery } from 'Deer/pages/MyData/Overview/cards/FeedbackOverview/dataHooks';
import { setIsCardOpen } from 'Deer/slice/DeerCollapsibleCard.slice';
import { GetDataPath, RowClickedEvent } from 'ag-grid-community';
import { useOpenCompetencyDrawer } from 'components/Develop/helpers/useOpenCompetencyDrawer';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';
import { checkCollapsibleCardsEnabled, checkNewNavEnabled } from 'utils/helpers/featureFlags';
import { getUniqueCardKey } from 'utils/helpers/getUniqueCardKey';

import competencyTableEmptyScreen from 'assets/assetsV1/illustrations/deerIllustrations/competencyTableEmptyScreen.svg';

import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';
import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { PaperWithShadow } from 'uiToolkitV1/Paper';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';

import { useGetMyCompetenciesQuery } from '../dataHooks';
import { useAgGridConfig } from './hooks/useAgGridConfig';
import { Row } from './types/myCompetencies';

const NoRowsOverlayComponent = () => (
  <EmptyScreen
    heading="Nothing to see here, yet!"
    description="Competencies will be shown here once your administrator configures them for your role."
    illustrationSrc={competencyTableEmptyScreen}
    illustrationStyle={{ marginTop: '90px' }}
  />
);

export const MyCompetencies = () => {
  const { isLoading } = useGetMyCompetenciesQuery();
  const spiderChartRef = useRef();
  const { l1Module } = useRouteToNavLevels();
  const { columnDefs, autoGroupColumnDef, defaultColDef, rowData, isTreeData } = useAgGridConfig() ?? {};
  const history = useHistory();
  const dispatch = useDispatch();

  const currentURL = history.location.pathname;

  const localStorageKeyForChart = getUniqueCardKey({ url: currentURL, cardId: 'myCompetenciesSpiderChart' });

  const isChartOpen = useLocalStorageCards({
    localStorageKey: localStorageKeyForChart,
    card: 'myCompetenciesSpiderChart',
    section: 'myData',
  });

  const { openCompetencyPanel } = useOpenCompetencyDrawer();

  const loggedInEmployeeId = useSelector(selectLoggedInEmployeeId);

  const { data: spiderChartData } = useGetSpiderChartQuery();

  const graphNodes = spiderChartData?.competencyGraphData?.map((competency) => ({
    id: competency.employeeId ? String(competency.employeeId) : 'COMPANY',
    name: competency.nodeType === 'COMPANY' ? 'Company average score' : 'Competency score',
  }));

  const graphSeries = spiderChartData?.competencyGraphData.map((item) => ({
    data: item.scores.map((score) => score.score),
    id: item.employeeId ? String(item.employeeId) : 'COMPANY',
  }));

  const competencyNames = spiderChartData?.competencyDetails?.map((value) => value.competencyName);

  const handleRowClicked = useCallback(
    (e: RowClickedEvent<Row>) => {
      if (!e.data.competency.uuid) return;
      openCompetencyPanel({
        tagId: e.data.competency.uuid,
        employeeId: isTreeData ? e.data.employee.value.id : loggedInEmployeeId,
      });
    },
    [isTreeData, loggedInEmployeeId, openCompetencyPanel]
  );

  const handleChartChange = (newVal) => {
    dispatch(
      setIsCardOpen({
        card: 'myCompetenciesSpiderChart',
        section: 'myData',
        isCardOpen: newVal,
        localStorageKey: localStorageKeyForChart,
      })
    );
  };

  const getDataPath = useCallback<GetDataPath<Row>>((data) => data.hierarchy?.value, []);

  const maxRating = spiderChartData?.maxRating;

  if (isLoading) {
    return <SkeletonLoader />;
  }

  return (
    <>
      {checkCollapsibleCardsEnabled() && l1Module === 'myData' && (
        <CollapsibleCard
          style={{
            marginBottom: '8px',
          }}
          open={isChartOpen}
          onOpenChange={handleChartChange}
          title="Distribution"
          disabled={!checkNewNavEnabled()}
          actions={<Button iconRight={Download} variant="text" onClick={() => spiderChartRef?.current?.download()} />}
        >
          <Box h="348px" borderRadius="8px" padding="8px" background="#F5F7F9">
            <RadarChart
              yAxisLabel=""
              ref={spiderChartRef}
              yAxisDomain={[0, maxRating ?? 100]}
              nodes={graphNodes}
              series={graphSeries}
              tooltipTitle="Competency score"
              xAxisLabel="Competencies"
              xAxisValues={competencyNames}
            />
          </Box>
        </CollapsibleCard>
      )}

      <PaperWithShadow px={3}>
        <AgGridTable
          defaultColDef={defaultColDef}
          autoGroupColumnDef={autoGroupColumnDef}
          columnDefs={columnDefs}
          rowData={rowData}
          kebabMenuIsRowSpecific={false}
          domLayout="autoHeight"
          rowHeight={52}
          containerAutoHeight
          pagination
          suppressPaginationPanel={rowData?.length < 6 || rowData?.length === undefined || rowData?.length === 0}
          paginationPageSize={10}
          showSidebarToggle={false}
          onRowClicked={handleRowClicked}
          treeData={isTreeData}
          getDataPath={getDataPath}
          noRowsOverlayComponent={NoRowsOverlayComponent}
        />
      </PaperWithShadow>
    </>
  );
};
